import { useMemo } from 'react'
import { fDate } from '@/utils/format-date'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import type { OrderPrivilege } from './order-privilege.types'

import {
  getOrderStatusLabel,
  getPaymentStatusLabel,
  getPaymentTypeLabel,
  getShippingStatusLabel,
  getOrderStatusBadgeClass,
  getPaymentStatusBadgeClass,
  getShippingStatusBadgeClass,
} from './helper'

interface OrderDetailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  order: OrderPrivilege | null
}

export default function OrderDetailDialog({
  open,
  onOpenChange,
  order,
}: OrderDetailDialogProps) {
  const basicInfo = useMemo(
    () =>
      order ? [{ label: 'เลขที่คำสั่งซื้อ', value: order.orderNumber }] : [],
    [order],
  )

  const statusInfo = useMemo(
    () =>
      order
        ? [
            {
              label: 'สถานะคำสั่งซื้อ',
              value: getOrderStatusLabel(order.status),
              isBadge: true,
            },
            {
              label: 'สถานะการชำระเงิน',
              value: getPaymentStatusLabel(order.paymentStatus),
              isBadge: true,
            },
            {
              label: 'ประเภทการชำระเงิน',
              value: getPaymentTypeLabel(order.paymentType),
            },
            {
              label: 'สถานะการจัดส่ง',
              value: getShippingStatusLabel(order.shippingStatus),
              isBadge: true,
            },
          ]
        : [],
    [order],
  )

  const shippingInfo = useMemo(
    () =>
      order
        ? [
            { label: 'เลขพัสดุ', value: order.trackingNumber || '-' },
            { label: 'หมายเหตุการจัดส่ง', value: order.shippingNote || '-' },
            {
              label: 'วันที่จัดส่ง',
              value: order.shippedAt ? fDate(order.shippedAt) : '-',
            },
            {
              label: 'วันที่ส่งถึง',
              value: order.deliveredAt ? fDate(order.deliveredAt) : '-',
            },
          ]
        : [],
    [order],
  )

  const paymentInfo = useMemo(
    () =>
      order
        ? [
            {
              label: 'วันที่หมดอายุการชำระเงิน',
              value: fDate(order.paymentExpiredAt),
            },
            {
              label: 'วันที่หมดอายุของสิทธิพิเศษ',
              value: fDate(order.privilegeExpiredAt),
            },
            {
              label: 'วันที่เริ่มใช้งานสิทธิพิเศษ',
              value: order.privilegeStartAt
                ? fDate(order.privilegeStartAt)
                : '-',
            },
          ]
        : [],
    [order],
  )

  const cashPaymentInfo = useMemo(
    () =>
      order?.cashPayment
        ? [
            { label: 'สกุลเงิน', value: order.cashPayment.currency },
            {
              label: 'จำนวนเงิน (บาท)',
              value: order.cashPayment.amount.toString(),
            },
            {
              label: 'ชำระแล้ว',
              value: order.cashPayment.isPaid ? 'ใช่' : 'ไม่',
            },
            {
              label: 'วันที่ชำระเงิน',
              value: order.cashPayment.paidAt
                ? fDate(order.cashPayment.paidAt)
                : '-',
            },
            {
              label: 'รหัส Transaction',
              value: order.cashPayment.transactionId || '-',
            },
            {
              label: 'รหัส Chillpay',
              value: order.cashPayment.chillpayTransactionId || '-',
            },
            {
              label: 'รหัสอ้างอิง',
              value: order.cashPayment.paymentReference || '-',
            },
          ]
        : [],
    [order],
  )

  const tokenPaymentInfo = useMemo(
    () =>
      order?.tokenPayments?.length
        ? order.tokenPayments.map((token, index) => ({
            label: `Token ${index + 1}`,
            value: `${token.tokenName} (${token.amount} ${token.tokenId})`,
            isPaid: token.isPaid,
            paidAt: token.paidAt,
          }))
        : [],
    [order],
  )

  const systemInfo = useMemo(
    () =>
      order
        ? [
            { label: 'หมายเหตุเพิ่มเติม', value: order.note || '-' },
            { label: 'วันที่สร้าง', value: fDate(order.createdAt) },
            { label: 'อัปเดตล่าสุด', value: fDate(order.updatedAt) },
          ]
        : [],
    [order],
  )

  const renderInfoSection = (
    title: string,
    items: Array<{ label: string; value: string; isBadge?: boolean }>,
    showBadges = false,
  ) => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        {items.map((item, index) => (
          <div key={item.label || index}>
            <div className="text-muted-foreground mb-1">{item.label}</div>
            <div className="font-medium">
              {showBadges && item.isBadge ? (
                <Badge
                  className={
                    item.label.includes('คำสั่งซื้อ')
                      ? getOrderStatusBadgeClass(
                          (order as OrderPrivilege).status,
                        )
                      : item.label.includes('การชำระเงิน') &&
                          !item.label.includes('ประเภท')
                        ? getPaymentStatusBadgeClass(
                            (order as OrderPrivilege).paymentStatus,
                          )
                        : item.label.includes('การจัดส่ง')
                          ? getShippingStatusBadgeClass(
                              (order as OrderPrivilege).shippingStatus,
                            )
                          : undefined
                  }
                >
                  {item.value}
                </Badge>
              ) : (
                <div className="truncate" title={String(item.value)}>
                  {String(item.value)}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  const renderTokenPayment = (
    token: { label: string; value: string; isPaid: boolean; paidAt?: Date },
    index: number,
  ) => (
    <div key={index} className="p-3 border rounded-lg">
      <div className="flex justify-between items-start mb-2">
        <span className="font-medium truncate flex-1 mr-2" title={token.label}>
          {token.label}
        </span>
        <Badge variant={token.isPaid ? 'default' : 'secondary'}>
          {token.isPaid ? 'ชำระแล้ว' : 'ยังไม่ชำระ'}
        </Badge>
      </div>
      <div
        className="text-sm text-muted-foreground truncate"
        title={token.value}
      >
        {token.value}
      </div>
      {token.paidAt && (
        <div className="text-xs text-muted-foreground mt-1">
          วันที่ชำระ: {fDate(token.paidAt)}
        </div>
      )}
    </div>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>รายละเอียดคำสั่งซื้อ</DialogTitle>
          <DialogDescription>
            แสดงรายละเอียดทั้งหมดของคำสั่งซื้อ
          </DialogDescription>
        </DialogHeader>

        {order && (
          <div className="space-y-6">
            {renderInfoSection('ข้อมูลพื้นฐาน', basicInfo)}
            <Separator />
            {renderInfoSection('สถานะต่างๆ', statusInfo, true)}
            <Separator />
            {renderInfoSection('ข้อมูลการจัดส่ง', shippingInfo)}
            <Separator />
            {renderInfoSection('ข้อมูลการชำระเงิน', paymentInfo)}

            {cashPaymentInfo.length > 0 && (
              <>
                <Separator />
                {renderInfoSection('ข้อมูลการชำระเงินสด', cashPaymentInfo)}
              </>
            )}

            {tokenPaymentInfo.length > 0 && (
              <>
                <Separator />
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">
                    ข้อมูลการชำระด้วย Token
                  </h3>
                  <div className="space-y-2">
                    {tokenPaymentInfo.map((token, index) =>
                      renderTokenPayment(token, index),
                    )}
                  </div>
                </div>
              </>
            )}

            <Separator />
            {renderInfoSection('ข้อมูลระบบ', systemInfo)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
